import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Card, Spin, Select, Table, Typography, Row, Col, Space, Tag, DatePicker, Switch } from 'antd';
import { 
  FunnelPlotOutlined, 
  TrophyOutlined, 
  LineChartOutlined, 
  TableOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const { Title: AntTitle, Text } = Typography;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Get the Monday of the week for a given date
 */
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Format a date as "MMM D" (e.g., "Dec 2")
 */
const formatShortDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Time period options
 */
const TIME_PERIODS = [
  { value: 1, label: 'Last 1 Week' },
  { value: 4, label: 'Last 4 Weeks' },
  { value: 8, label: 'Last 8 Weeks' },
  { value: 12, label: 'Last 12 Weeks' },
  { value: 26, label: 'Last 6 Months' },
];

/**
 * Calculate statistics from deals and stages data
 * @param {Array} deals - Array of deal objects
 * @param {Array} stages - Array of stage objects
 * @param {number} weekCount - Number of weeks in the period
 * @param {Date} periodStartDate - Start date of the period (Monday of the first week). If null, calculates from current date backwards
 */
const calculateStatistics = (deals, stages, weekCount, periodStartDate = null) => {
  if (!deals?.length) {
    return {
      currentPipeline: [],
      totalActive: 0,
      totalWon: 0,
      totalLost: 0,
      weeklyTrend: [],
      periodSummary: { newDeals: 0, wonDeals: 0, lostDeals: 0 },
      periodStartDate: null,
      periodEndDate: null,
    };
  }

  // Create a map of stage_id to stage info
  const stageMap = {};
  const wonStageIds = new Set();
  const lostStageIds = new Set();
  
  // Stage ID for "Nezpůsobilý" (Unqualified) - should be treated as lost
  const UNQUALIFIED_STAGE_ID = 11368654;
  
  stages?.forEach((stage) => {
    stageMap[stage.id] = stage;
    if (stage.category === 'won' || stage.isWonStage) {
      wonStageIds.add(stage.id);
    }
    // Check if stage name indicates it's a lost/unqualified stage
    if (stage.name && (stage.name.toLowerCase().includes('nezpůsobilý') || 
                       stage.name.toLowerCase().includes('unqualified') ||
                       stage.name.toLowerCase().includes('ztracený') ||
                       stage.name.toLowerCase().includes('lost'))) {
      lostStageIds.add(stage.id);
    }
  });
  
  // Always include the known unqualified stage ID
  lostStageIds.add(UNQUALIFIED_STAGE_ID);

  // Initialize counters
  const stageCounters = {};
  let totalWon = 0;
  let totalLost = 0;
  let totalActive = 0;

  // Weekly trend tracking
  const weeklyData = {};
  
  // Calculate period start and end dates
  let actualPeriodStartDate;
  if (periodStartDate) {
    actualPeriodStartDate = getWeekStart(new Date(periodStartDate));
  } else {
    // Default behavior: calculate from current date backwards
    const now = new Date();
    actualPeriodStartDate = getWeekStart(new Date(now.getTime() - (weekCount - 1) * 7 * 24 * 60 * 60 * 1000));
  }
  
  // Calculate period end date (end of the last week in the period)
  const periodEndDate = new Date(actualPeriodStartDate);
  periodEndDate.setDate(periodEndDate.getDate() + (weekCount * 7) - 1);
  periodEndDate.setHours(23, 59, 59, 999);
  
  // Initialize weeks for the selected period
  for (let i = 0; i < weekCount; i++) {
    const weekStart = new Date(actualPeriodStartDate);
    weekStart.setDate(weekStart.getDate() + (i * 7));
    const weekStartMonday = getWeekStart(weekStart);
    const weekKey = weekStartMonday.toISOString().split('T')[0];
    weeklyData[weekKey] = {
      weekStart: weekStartMonday,
      weekKey,
      label: formatShortDate(weekStartMonday),
      newDeals: 0,
      wonDeals: 0,
      lostDeals: 0,
    };
  }

  // Period summary
  let periodNewDeals = 0;
  let periodWonDeals = 0;
  let periodLostDeals = 0;

  // Process each deal
  deals.forEach((deal) => {
    const hasLossReason = deal.loss_reason_id != null;
    const hasUnqualifiedReason = deal.unqualified_reason_id != null;
    const isInLostStage = lostStageIds.has(deal.stage_id);
    const isWon = wonStageIds.has(deal.stage_id);
    
    // A deal is considered lost if:
    // 1. It has a loss_reason_id, OR
    // 2. It has an unqualified_reason_id, OR
    // 3. It's in a "lost" stage (like "Nezpůsobilý" / Unqualified)
    const isLost = hasLossReason || hasUnqualifiedReason || isInLostStage;

    // Count ALL TIME totals
    if (isLost) {
      totalLost++;
    } else if (isWon) {
      totalWon++;
    } else {
      totalActive++;
      // Count by stage (only for active deals)
      const stageId = deal.stage_id;
      if (!stageCounters[stageId]) {
        stageCounters[stageId] = {
          stageId,
          name: stageMap[stageId]?.name || `Stage ${stageId}`,
          count: 0,
          position: stageMap[stageId]?.position || 999,
        };
      }
      stageCounters[stageId].count++;
    }

    // Track WEEKLY activity (within selected period)
    // New deals - by created_at
    if (deal.created_at) {
      const createdDate = new Date(deal.created_at);
      if (createdDate >= actualPeriodStartDate && createdDate <= periodEndDate) {
        const createdWeekStart = getWeekStart(createdDate);
        const createdWeekKey = createdWeekStart.toISOString().split('T')[0];
        if (weeklyData[createdWeekKey]) {
          weeklyData[createdWeekKey].newDeals++;
          periodNewDeals++;
        }
      }
    }

    // Won deals - by last_stage_change_at (if currently in won stage)
    if (isWon && deal.last_stage_change_at) {
      const wonDate = new Date(deal.last_stage_change_at);
      if (wonDate >= actualPeriodStartDate && wonDate <= periodEndDate) {
        const wonWeekStart = getWeekStart(wonDate);
        const wonWeekKey = wonWeekStart.toISOString().split('T')[0];
        if (weeklyData[wonWeekKey]) {
          weeklyData[wonWeekKey].wonDeals++;
          periodWonDeals++;
        }
      }
    }

    // Lost deals - approximate using updated_at or last_stage_change_at
    if (isLost) {
      // Use last_stage_change_at if available (more accurate for stage changes)
      // Otherwise fall back to updated_at
      const lostDateStr = deal.last_stage_change_at || deal.updated_at;
      if (lostDateStr) {
        const lostDate = new Date(lostDateStr);
        if (lostDate >= actualPeriodStartDate && lostDate <= periodEndDate) {
          const lostWeekStart = getWeekStart(lostDate);
          const lostWeekKey = lostWeekStart.toISOString().split('T')[0];
          if (weeklyData[lostWeekKey]) {
            weeklyData[lostWeekKey].lostDeals++;
            periodLostDeals++;
          }
        }
      }
    }
  });

  // Convert stage counters to sorted array
  const currentPipeline = Object.values(stageCounters).sort((a, b) => a.position - b.position);

  // Convert weekly data to sorted array
  const weeklyTrend = Object.values(weeklyData).sort(
    (a, b) => new Date(a.weekKey) - new Date(b.weekKey)
  );

  return {
    currentPipeline,
    totalActive,
    totalWon,
    totalLost,
    total: deals.length,
    weeklyTrend,
    periodSummary: {
      newDeals: periodNewDeals,
      wonDeals: periodWonDeals,
      lostDeals: periodLostDeals,
    },
    periodStartDate: actualPeriodStartDate,
    periodEndDate: periodEndDate,
  };
};

/**
 * DealStatistics component - displays deal statistics and charts
 */
const DealStatistics = ({ deals, stages, isLoading }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(12); // Default: 12 weeks
  const [useCustomDate, setUseCustomDate] = useState(false); // Toggle between custom date and "last X weeks"
  const [selectedStartDate, setSelectedStartDate] = useState(null); // Selected period start date

  // Calculate the selected period start date (Monday of the selected week)
  const periodStartDate = useMemo(() => {
    if (!useCustomDate || !selectedStartDate) {
      return null; // Use default "last X weeks" behavior
    }
    return getWeekStart(new Date(selectedStartDate));
  }, [useCustomDate, selectedStartDate]);

  // Calculate previous period (same length, ending just before selected period)
  const previousPeriodStartDate = useMemo(() => {
    if (!periodStartDate) return null;
    const prevStart = new Date(periodStartDate);
    prevStart.setDate(prevStart.getDate() - (selectedPeriod * 7));
    return prevStart;
  }, [periodStartDate, selectedPeriod]);

  // Calculate statistics for selected period
  const stats = useMemo(
    () => calculateStatistics(deals, stages, selectedPeriod, periodStartDate),
    [deals, stages, selectedPeriod, periodStartDate]
  );

  // Calculate statistics for previous period (for comparison)
  const previousStats = useMemo(() => {
    if (!previousPeriodStartDate) return null;
    return calculateStatistics(deals, stages, selectedPeriod, previousPeriodStartDate);
  }, [deals, stages, selectedPeriod, previousPeriodStartDate]);

  // Calculate comparison metrics
  const comparison = useMemo(() => {
    if (!previousStats) return null;
    
    const calculateChange = (current, previous) => {
      const diff = current - previous;
      const percent = previous > 0 ? ((diff / previous) * 100) : (current > 0 ? 100 : 0);
      return { diff, percent, isPositive: diff > 0, isNegative: diff < 0 };
    };

    return {
      newDeals: calculateChange(stats.periodSummary.newDeals, previousStats.periodSummary.newDeals),
      wonDeals: calculateChange(stats.periodSummary.wonDeals, previousStats.periodSummary.wonDeals),
      lostDeals: calculateChange(stats.periodSummary.lostDeals, previousStats.periodSummary.lostDeals),
    };
  }, [stats, previousStats]);

  // Get period label for display
  const periodLabel = useMemo(() => {
    if (useCustomDate && periodStartDate) {
      const endDate = new Date(periodStartDate);
      endDate.setDate(endDate.getDate() + (selectedPeriod * 7) - 1);
      return `${formatShortDate(periodStartDate)} - ${formatShortDate(endDate)}`;
    }
    return TIME_PERIODS.find(p => p.value === selectedPeriod)?.label || `Last ${selectedPeriod} Weeks`;
  }, [useCustomDate, periodStartDate, selectedPeriod]);

  // Get previous period label
  const previousPeriodLabel = useMemo(() => {
    if (!previousPeriodStartDate) return null;
    const prevEndDate = new Date(previousPeriodStartDate);
    prevEndDate.setDate(prevEndDate.getDate() + (selectedPeriod * 7) - 1);
    return `${formatShortDate(previousPeriodStartDate)} - ${formatShortDate(prevEndDate)}`;
  }, [previousPeriodStartDate, selectedPeriod]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <Spin size="large" tip="Calculating statistics..." />
      </div>
    );
  }

  // Pipeline chart data (horizontal bar)
  const pipelineChartData = {
    labels: stats.currentPipeline.map((s) => s.name),
    datasets: [
      {
        label: 'Active Deals',
        data: stats.currentPipeline.map((s) => s.count),
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const pipelineChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      x: { 
        beginAtZero: true,
        ticks: { stepSize: 1 }
      },
    },
  };

  // Weekly trend chart data
  const trendChartData = {
    labels: stats.weeklyTrend.map((w) => w.label),
    datasets: [
      {
        label: 'New Deals',
        data: stats.weeklyTrend.map((w) => w.newDeals),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Won',
        data: stats.weeklyTrend.map((w) => w.wonDeals),
        borderColor: 'rgba(40, 167, 69, 1)',
        backgroundColor: 'transparent',
        tension: 0.3,
        borderDash: [5, 5],
      },
      {
        label: 'Lost',
        data: stats.weeklyTrend.map((w) => w.lostDeals),
        borderColor: 'rgba(220, 53, 69, 1)',
        backgroundColor: 'transparent',
        tension: 0.3,
        borderDash: [5, 5],
      },
      // Previous period data (if available) - align by index since both periods have same length
      ...(previousStats && previousStats.weeklyTrend.length === stats.weeklyTrend.length ? [
        {
          label: 'New Deals (Previous)',
          data: previousStats.weeklyTrend.map((w) => w.newDeals),
          borderColor: 'rgba(54, 162, 235, 0.5)',
          backgroundColor: 'transparent',
          tension: 0.3,
          borderDash: [10, 5],
          pointRadius: 3,
        },
        {
          label: 'Won (Previous)',
          data: previousStats.weeklyTrend.map((w) => w.wonDeals),
          borderColor: 'rgba(40, 167, 69, 0.5)',
          backgroundColor: 'transparent',
          tension: 0.3,
          borderDash: [10, 5],
          pointRadius: 3,
        },
        {
          label: 'Lost (Previous)',
          data: previousStats.weeklyTrend.map((w) => w.lostDeals),
          borderColor: 'rgba(220, 53, 69, 0.5)',
          backgroundColor: 'transparent',
          tension: 0.3,
          borderDash: [10, 5],
          pointRadius: 3,
        },
      ] : []),
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
    },
    scales: {
      y: { 
        beginAtZero: true,
        ticks: { stepSize: 1 }
      },
    },
  };

  // Pipeline table columns
  const pipelineColumns = [
    {
      title: 'Stage',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      render: (count) => <Text strong>{count}</Text>,
    },
  ];

  const pipelineTableData = [
    ...stats.currentPipeline.map((stage) => ({
      key: stage.stageId,
      name: stage.name,
      count: stage.count,
    })),
    {
      key: 'total',
      name: <Text strong>Total Active</Text>,
      count: <Text strong>{stats.totalActive}</Text>,
    },
  ];

  return (
    <div>
      {/* Section 1: Current Pipeline Snapshot */}
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <FunnelPlotOutlined />
            <span>Current Pipeline</span>
          </Space>
        }
        extra={<Tag color="primary" style={{ fontSize: 16 }}>{stats.totalActive} Active</Tag>}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Active deals right now, by stage
        </Text>
        {stats.currentPipeline.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '32px 0' }}>
            No active deals in pipeline
          </div>
        ) : (
          <Row gutter={16}>
            <Col xs={24} lg={16}>
              <div style={{ height: `${Math.max(200, stats.currentPipeline.length * 50)}px` }}>
                <Bar data={pipelineChartData} options={pipelineChartOptions} />
              </div>
            </Col>
            <Col xs={24} lg={8}>
              <Table
                columns={pipelineColumns}
                dataSource={pipelineTableData}
                pagination={false}
                size="small"
                showHeader={false}
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* Section 2: All-Time Summary */}
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <TrophyOutlined />
            <span>All-Time Summary</span>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Total deals since tracking began
        </Text>
        <Row gutter={16} style={{ textAlign: 'center' }}>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, borderRadius: 4, backgroundColor: 'rgba(82, 196, 26, 0.1)' }}>
              <AntTitle level={2} style={{ margin: 0, color: '#52c41a' }}>{stats.totalWon}</AntTitle>
              <Text type="secondary">Won Deals</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, borderRadius: 4, backgroundColor: 'rgba(255, 77, 79, 0.1)' }}>
              <AntTitle level={2} style={{ margin: 0, color: '#ff4d4f' }}>{stats.totalLost}</AntTitle>
              <Text type="secondary">Lost Deals</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 16, borderRadius: 4, backgroundColor: 'rgba(140, 140, 140, 0.1)' }}>
              <AntTitle level={2} style={{ margin: 0, color: '#8c8c8c' }}>{stats.total}</AntTitle>
              <Text type="secondary">Total Deals</Text>
            </div>
          </Col>
        </Row>
        {stats.totalWon + stats.totalLost > 0 && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text type="secondary">
              Win Rate: <Text strong>{((stats.totalWon / (stats.totalWon + stats.totalLost)) * 100).toFixed(1)}%</Text>
              {' '}({stats.totalWon} won out of {stats.totalWon + stats.totalLost} closed)
            </Text>
          </div>
        )}
      </Card>

      {/* Section 3: Activity Over Time */}
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <LineChartOutlined />
            <span>Activity Over Time</span>
          </Space>
        }
        extra={
          <Space orientation="vertical" align="end" size="small">
            <Space>
              <Text type="secondary">Custom Date:</Text>
              <Switch
                checked={useCustomDate}
                onChange={setUseCustomDate}
                checkedChildren={<CalendarOutlined />}
                unCheckedChildren="Auto"
              />
            </Space>
            <Space>
              <Text type="secondary">Period:</Text>
              <Select
                value={selectedPeriod}
                onChange={setSelectedPeriod}
                style={{ width: 150 }}
                options={TIME_PERIODS.map((period) => ({
                  value: period.value,
                  label: period.label,
                }))}
              />
            </Space>
            {useCustomDate && (
              <Space>
                <Text type="secondary">Start Week:</Text>
                <DatePicker
                  value={selectedStartDate ? dayjs(selectedStartDate) : null}
                  onChange={(date) => {
                    if (date) {
                      // Get Monday of the selected week
                      const monday = date.startOf('week');
                      setSelectedStartDate(monday.toDate());
                    } else {
                      setSelectedStartDate(null);
                    }
                  }}
                  picker="week"
                  format="MMM D, YYYY"
                  placeholder="Select week"
                />
              </Space>
            )}
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {useCustomDate && previousPeriodLabel ? (
            <>Comparing: <Text strong>{periodLabel}</Text> vs <Text strong>{previousPeriodLabel}</Text></>
          ) : (
            <>Weekly deal activity - {periodLabel}</>
          )}
        </Text>
        {/* Period Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16, textAlign: 'center' }}>
              <AntTitle level={3} style={{ margin: 0, color: '#1890ff' }}>
                {stats.periodSummary.newDeals}
                {comparison?.newDeals && (
                  <span style={{ fontSize: 16, marginLeft: 8 }}>
                    {comparison.newDeals.isPositive ? (
                      <Text type="success">
                        <ArrowUpOutlined /> +{comparison.newDeals.diff}
                      </Text>
                    ) : comparison.newDeals.isNegative ? (
                      <Text type="danger">
                        <ArrowDownOutlined /> {comparison.newDeals.diff}
                      </Text>
                    ) : (
                      <Text type="secondary">0</Text>
                    )}
                  </span>
                )}
              </AntTitle>
              <Text type="secondary" style={{ fontSize: 12 }}>
                New Deals ({periodLabel})
                {comparison?.newDeals && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    {comparison.newDeals.percent !== 0 && (
                      <Text type={comparison.newDeals.isPositive ? 'success' : comparison.newDeals.isNegative ? 'danger' : 'secondary'}>
                        {comparison.newDeals.isPositive ? '+' : ''}{comparison.newDeals.percent.toFixed(1)}% vs previous
                      </Text>
                    )}
                  </div>
                )}
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16, textAlign: 'center' }}>
              <AntTitle level={3} style={{ margin: 0, color: '#52c41a' }}>
                +{stats.periodSummary.wonDeals}
                {comparison?.wonDeals && (
                  <span style={{ fontSize: 16, marginLeft: 8 }}>
                    {comparison.wonDeals.isPositive ? (
                      <Text type="success">
                        <ArrowUpOutlined /> +{comparison.wonDeals.diff}
                      </Text>
                    ) : comparison.wonDeals.isNegative ? (
                      <Text type="danger">
                        <ArrowDownOutlined /> {comparison.wonDeals.diff}
                      </Text>
                    ) : (
                      <Text type="secondary">0</Text>
                    )}
                  </span>
                )}
              </AntTitle>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Won ({periodLabel})
                {comparison?.wonDeals && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    {comparison.wonDeals.percent !== 0 && (
                      <Text type={comparison.wonDeals.isPositive ? 'success' : comparison.wonDeals.isNegative ? 'danger' : 'secondary'}>
                        {comparison.wonDeals.isPositive ? '+' : ''}{comparison.wonDeals.percent.toFixed(1)}% vs previous
                      </Text>
                    )}
                  </div>
                )}
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16, textAlign: 'center' }}>
              <AntTitle level={3} style={{ margin: 0, color: '#ff4d4f' }}>
                -{stats.periodSummary.lostDeals}
                {comparison?.lostDeals && (
                  <span style={{ fontSize: 16, marginLeft: 8 }}>
                    {comparison.lostDeals.isPositive ? (
                      <Text type="danger">
                        <ArrowUpOutlined /> +{comparison.lostDeals.diff}
                      </Text>
                    ) : comparison.lostDeals.isNegative ? (
                      <Text type="success">
                        <ArrowDownOutlined /> {comparison.lostDeals.diff}
                      </Text>
                    ) : (
                      <Text type="secondary">0</Text>
                    )}
                  </span>
                )}
              </AntTitle>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Lost ({periodLabel})
                {comparison?.lostDeals && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    {comparison.lostDeals.percent !== 0 && (
                      <Text type={comparison.lostDeals.isPositive ? 'danger' : comparison.lostDeals.isNegative ? 'success' : 'secondary'}>
                        {comparison.lostDeals.isPositive ? '+' : ''}{comparison.lostDeals.percent.toFixed(1)}% vs previous
                      </Text>
                    )}
                  </div>
                )}
              </Text>
            </div>
          </Col>
        </Row>

        {/* Trend Chart */}
        <div style={{ height: '300px' }}>
          <Line data={trendChartData} options={trendChartOptions} />
        </div>
      </Card>

      {/* Section 4: Weekly Breakdown Table */}
      <Card
        title={
          <Space>
            <TableOutlined />
            <span>Weekly Breakdown</span>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {periodLabel} - detailed view
        </Text>
        <Table
          columns={[
            {
              title: 'Week Starting',
              dataIndex: 'label',
              key: 'label',
            },
            {
              title: 'New',
              dataIndex: 'newDeals',
              key: 'newDeals',
              align: 'right',
              render: (value) => value || '-',
            },
            {
              title: 'Won',
              dataIndex: 'wonDeals',
              key: 'wonDeals',
              align: 'right',
              render: (value) => value ? <Text type="success">{value}</Text> : '-',
            },
            {
              title: 'Lost',
              dataIndex: 'lostDeals',
              key: 'lostDeals',
              align: 'right',
              render: (value) => value ? <Text type="danger">{value}</Text> : '-',
            },
            {
              title: 'Net',
              key: 'net',
              align: 'right',
              render: (_, record) => {
                const netChange = record.newDeals - record.wonDeals - record.lostDeals;
                const hasActivity = record.newDeals > 0 || record.wonDeals > 0 || record.lostDeals > 0;
                if (!hasActivity) return '-';
                return (
                  <Text 
                    strong
                    style={{ 
                      color: netChange > 0 ? '#52c41a' : netChange < 0 ? '#ff4d4f' : undefined 
                    }}
                  >
                    {netChange >= 0 ? '+' : ''}{netChange}
                  </Text>
                );
              },
            },
          ]}
          dataSource={[...stats.weeklyTrend].reverse().map((week) => {
            const hasActivity = week.newDeals > 0 || week.wonDeals > 0 || week.lostDeals > 0;
            return {
              key: week.weekKey,
              label: hasActivity ? week.label : <Text type="secondary">{week.label}</Text>,
              newDeals: week.newDeals,
              wonDeals: week.wonDeals,
              lostDeals: week.lostDeals,
              hasActivity,
            };
          })}
          pagination={false}
          size="small"
          summary={() => {
            const net = stats.periodSummary.newDeals - stats.periodSummary.wonDeals - stats.periodSummary.lostDeals;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell>
                    <Text strong>Total ({periodLabel})</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text strong>{stats.periodSummary.newDeals}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text strong type="success">{stats.periodSummary.wonDeals}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text strong type="danger">{stats.periodSummary.lostDeals}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text 
                      strong
                      style={{ 
                        color: net > 0 ? '#52c41a' : net < 0 ? '#ff4d4f' : undefined 
                      }}
                    >
                      {net >= 0 ? '+' : ''}{net}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Data freshness note */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <InfoCircleOutlined style={{ marginRight: 4 }} />
          Data sourced from Zendesk Sell. Weekly activity is approximated from deal timestamps.
        </Text>
      </div>
    </div>
  );
};

export default DealStatistics;
